import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Building, Calendar, Clock, ArrowRight, HardHat, RefreshCw } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { calculerJoursOuvres } from '@/lib/heures-utils';

interface Membre {
  id: string;
  prenom: string;
  nom: string;
}

interface Affectation {
  id: string;
  chantier_id: string;
  date_debut: string;
  date_fin: string;
  heures_par_jour: number | null;
  chantiers?: { nom_chantier: string } | null;
}

interface RetraitData {
  affectationId: string;
  heures: number;
}

interface Chantier {
  id: string;
  nom_chantier?: string;
  reference_chantier?: string;
  date_debut_prevue?: string;
  date_fin_estimee?: string;
}

interface NouvelleAffectation {
  chantierId: string;
  dateDebut: string;
  dateFin: string;
  heuresJour1: number; // Heures pour le premier jour (jour de rotation)
  heuresJoursSuivants: number; // Heures pour les jours suivants
  joursOuvres: number;
}

interface ModalRetraitChantierProps {
  isOpen: boolean;
  onClose: () => void;
  membre: Membre | null;
  affectationsActives: Affectation[];
  nouveauStatut: 'disponible' | 'repos' | 'en_arret' | 'en_rotation';
  onConfirm: (retraits: RetraitData[], nouvelleAffectation?: NouvelleAffectation) => Promise<void>;
  // Props pour le mode rotation
  isRotationMode?: boolean;
  chantiersDisponibles?: Chantier[];
}

const STATUT_LABELS: Record<string, string> = {
  disponible: 'Disponible',
  repos: 'Repos',
  en_arret: 'En arrêt',
  en_rotation: 'En rotation',
  sur_chantier: 'Sur chantier',
};

export function ModalRetraitChantier({
  isOpen,
  onClose,
  membre,
  affectationsActives,
  nouveauStatut,
  onConfirm,
  isRotationMode = false,
  chantiersDisponibles = [],
}: ModalRetraitChantierProps) {
  const [isLoading, setIsLoading] = useState(false);

  // État pour les heures de chaque affectation (clé = affectation.id)
  const [heuresParAffectation, setHeuresParAffectation] = useState<Record<string, number>>({});

  // État pour les affectations sélectionnées (si plusieurs)
  const [selectedAffectations, setSelectedAffectations] = useState<Set<string>>(new Set());

  // États pour la nouvelle affectation (mode rotation)
  const [nouveauChantierId, setNouveauChantierId] = useState('');
  const [nouvelleDateDebut, setNouvelleDateDebut] = useState('');
  const [nouvelleDateFin, setNouvelleDateFin] = useState('');
  const [heuresJour1, setHeuresJour1] = useState(4); // Heures pour aujourd'hui (jour de rotation)
  const [heuresJoursSuivants, setHeuresJoursSuivants] = useState(8); // Heures pour les jours suivants
  const [joursOuvres, setJoursOuvres] = useState(0);

  // Initialiser les heures par défaut et la sélection
  useMemo(() => {
    const defaultHeures: Record<string, number> = {};
    const defaultSelection = new Set<string>();

    affectationsActives.forEach((a) => {
      defaultHeures[a.id] = a.heures_par_jour ?? 8;
      defaultSelection.add(a.id);
    });

    setHeuresParAffectation(defaultHeures);
    setSelectedAffectations(defaultSelection);
  }, [affectationsActives]);

  // Réinitialiser les états du nouveau chantier quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && isRotationMode) {
      const today = new Date().toISOString().split('T')[0];
      setNouveauChantierId('');
      setNouvelleDateDebut(today);
      setNouvelleDateFin('');
      setHeuresJour1(4); // Par défaut 4h le jour de rotation (l'autre moitié sur l'ancien chantier)
      setHeuresJoursSuivants(8);
      setJoursOuvres(1);
    }
  }, [isOpen, isRotationMode]);

  // Pré-remplir les dates quand un chantier est sélectionné
  useEffect(() => {
    if (nouveauChantierId && chantiersDisponibles.length > 0) {
      const chantier = chantiersDisponibles.find(c => c.id === nouveauChantierId);
      if (chantier) {
        const today = new Date().toISOString().split('T')[0];
        // Date de début = aujourd'hui ou date de début du chantier si dans le futur
        const dateDebutChantier = chantier.date_debut_prevue || today;
        const dateDebut = dateDebutChantier > today ? dateDebutChantier : today;
        setNouvelleDateDebut(dateDebut);

        // Date de fin = date de fin estimée du chantier
        if (chantier.date_fin_estimee) {
          setNouvelleDateFin(chantier.date_fin_estimee);
        }
      }
    }
  }, [nouveauChantierId, chantiersDisponibles]);

  // Calculer les jours ouvrés quand les dates changent
  useEffect(() => {
    if (nouvelleDateDebut && nouvelleDateFin && nouvelleDateDebut <= nouvelleDateFin) {
      const jours = calculerJoursOuvres(nouvelleDateDebut, nouvelleDateFin);
      setJoursOuvres(jours);
    } else {
      setJoursOuvres(0);
    }
  }, [nouvelleDateDebut, nouvelleDateFin]);

  const handleHeuresChange = (affectationId: string, value: string) => {
    const heures = Math.min(16, Math.max(0, parseFloat(value) || 0));
    setHeuresParAffectation((prev) => ({ ...prev, [affectationId]: heures }));
  };

  const toggleAffectation = (affectationId: string) => {
    setSelectedAffectations((prev) => {
      const next = new Set(prev);
      if (next.has(affectationId)) {
        next.delete(affectationId);
      } else {
        next.add(affectationId);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedAffectations.size === 0) return;

    // En mode rotation, vérifier que le nouveau chantier est sélectionné
    if (isRotationMode && !nouveauChantierId) return;

    setIsLoading(true);
    try {
      const retraits: RetraitData[] = Array.from(selectedAffectations).map((id) => ({
        affectationId: id,
        heures: heuresParAffectation[id] ?? 8,
      }));

      // Préparer la nouvelle affectation si en mode rotation
      const nouvelleAffectation: NouvelleAffectation | undefined = isRotationMode && nouveauChantierId
        ? {
            chantierId: nouveauChantierId,
            dateDebut: nouvelleDateDebut,
            dateFin: nouvelleDateFin,
            heuresJour1,
            heuresJoursSuivants,
            joursOuvres,
          }
        : undefined;

      await onConfirm(retraits, nouvelleAffectation);
    } finally {
      setIsLoading(false);
    }
  };

  // Validation: le bouton est désactivé si les données ne sont pas complètes
  const canConfirm = selectedAffectations.size > 0 &&
    (!isRotationMode || (nouveauChantierId && nouvelleDateDebut && nouvelleDateFin && joursOuvres > 0));

  if (!membre) return null;

  const isSingleAffectation = affectationsActives.length === 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={isRotationMode ? "max-w-xl max-h-[90vh] overflow-y-auto" : "max-w-lg"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            {isRotationMode ? (
              <>
                <RefreshCw className="h-5 w-5" />
                Rotation vers un nouveau chantier
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5" />
                Retrait du chantier
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isRotationMode
              ? `${membre.prenom} ${membre.nom} va être transféré vers un nouveau chantier.`
              : `${membre.prenom} ${membre.nom} va passer en statut "${STATUT_LABELS[nouveauStatut]}".`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Info membre */}
          <p className="text-sm text-muted-foreground">
            {isSingleAffectation
              ? 'Ce membre est actuellement affecté à :'
              : `Ce membre est actuellement affecté à ${affectationsActives.length} chantiers :`}
          </p>

          {/* Liste des affectations */}
          <div className="space-y-3">
            {affectationsActives.map((affectation) => {
              const nomChantier = affectation.chantiers?.nom_chantier || 'Chantier inconnu';
              const duree = formatDistanceToNow(parseISO(affectation.date_debut), {
                locale: fr,
                addSuffix: false,
              });
              const isSelected = selectedAffectations.has(affectation.id);

              return (
                <div
                  key={affectation.id}
                  className={`p-4 rounded-lg border transition-colors ${
                    isSelected ? 'border-primary bg-primary/5' : 'border-muted bg-muted/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox si plusieurs affectations */}
                    {!isSingleAffectation && (
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleAffectation(affectation.id)}
                        className="mt-1"
                      />
                    )}

                    <div className="flex-1 space-y-2">
                      {/* Nom du chantier */}
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-primary" />
                        <span className="font-semibold">{nomChantier}</span>
                      </div>

                      {/* Date de début */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>
                          Depuis le{' '}
                          {new Date(affectation.date_debut).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                          })}{' '}
                          ({duree})
                        </span>
                      </div>

                      {/* Input heures du dernier jour */}
                      {(isSingleAffectation || isSelected) && (
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          <Label htmlFor={`heures-${affectation.id}`} className="text-sm">
                            <Clock className="h-3.5 w-3.5 inline mr-1" />
                            Heures sur {nomChantier} aujourd'hui :
                          </Label>
                          <div className="flex items-center gap-1">
                            <Input
                              id={`heures-${affectation.id}`}
                              type="number"
                              min={0}
                              max={16}
                              step={0.5}
                              value={heuresParAffectation[affectation.id] ?? 8}
                              onChange={(e) => handleHeuresChange(affectation.id, e.target.value)}
                              className="w-16 h-8 text-center"
                            />
                            <span className="text-sm text-muted-foreground">h</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info supplémentaire */}
          <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <span className="text-lg">💡</span>
            <p className="text-xs text-muted-foreground">
              Si vous mettez <strong>0h</strong>, seuls les jours précédents seront comptabilisés
              dans les coûts du chantier.
            </p>
          </div>

          {/* Section nouvelle affectation (mode rotation uniquement) */}
          {isRotationMode && (
            <>
              {/* Séparateur */}
              <div className="flex items-center gap-3 pt-2">
                <div className="flex-1 border-t" />
                <div className="flex items-center gap-2 text-primary">
                  <ArrowRight className="h-4 w-4" />
                  <span className="text-sm font-semibold">Nouvelle affectation</span>
                </div>
                <div className="flex-1 border-t" />
              </div>

              {/* Sélection du nouveau chantier */}
              <div className="space-y-4 p-4 rounded-lg border border-primary/30 bg-primary/5">
                <div className="space-y-2">
                  <Label htmlFor="nouveau-chantier" className="flex items-center gap-2">
                    <HardHat className="h-4 w-4 text-primary" />
                    Nouveau chantier
                  </Label>
                  {chantiersDisponibles.length === 0 ? (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-600">
                      Aucun chantier disponible. Créez un chantier d'abord.
                    </div>
                  ) : (
                    <Select value={nouveauChantierId} onValueChange={setNouveauChantierId}>
                      <SelectTrigger id="nouveau-chantier">
                        <SelectValue placeholder="Sélectionner un chantier..." />
                      </SelectTrigger>
                      <SelectContent>
                        {chantiersDisponibles.map((chantier) => (
                          <SelectItem key={chantier.id} value={chantier.id}>
                            {chantier.reference_chantier || chantier.nom_chantier || 'Sans nom'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Dates de la nouvelle affectation */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="date-debut" className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5" />
                      Date de début
                    </Label>
                    <Input
                      id="date-debut"
                      type="date"
                      value={nouvelleDateDebut}
                      onChange={(e) => setNouvelleDateDebut(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date-fin" className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5" />
                      Date de fin
                    </Label>
                    <Input
                      id="date-fin"
                      type="date"
                      value={nouvelleDateFin}
                      min={nouvelleDateDebut}
                      onChange={(e) => setNouvelleDateFin(e.target.value)}
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Jours ouvrés */}
                <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                  <span className="text-sm text-muted-foreground">Durée :</span>
                  <span className="text-sm font-semibold">
                    {joursOuvres} jour{joursOuvres > 1 ? 's' : ''} travaillé{joursOuvres > 1 ? 's' : ''}
                  </span>
                </div>

                {/* Heures par jour - avec distinction jour 1 / jours suivants */}
                {joursOuvres === 1 ? (
                  // Si 1 seul jour, on demande juste les heures
                  <div className="space-y-2">
                    <Label htmlFor="heures-jour1" className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5" />
                      Heures sur le nouveau chantier aujourd'hui
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="heures-jour1"
                        type="number"
                        min={0.5}
                        max={16}
                        step={0.5}
                        value={heuresJour1}
                        onChange={(e) => setHeuresJour1(Math.min(16, Math.max(0.5, parseFloat(e.target.value) || 4)))}
                        className="h-9 w-20"
                      />
                      <span className="text-sm text-muted-foreground">heures</span>
                    </div>
                  </div>
                ) : joursOuvres > 1 ? (
                  // Si plusieurs jours, on distingue jour 1 et jours suivants
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                      <Label htmlFor="heures-jour1" className="flex items-center gap-2 text-amber-600">
                        <Clock className="h-3.5 w-3.5" />
                        Heures sur le nouveau chantier aujourd'hui
                      </Label>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          id="heures-jour1"
                          type="number"
                          min={0.5}
                          max={16}
                          step={0.5}
                          value={heuresJour1}
                          onChange={(e) => setHeuresJour1(Math.min(16, Math.max(0.5, parseFloat(e.target.value) || 4)))}
                          className="h-9 w-20"
                        />
                        <span className="text-sm text-muted-foreground">heures</span>
                      </div>
                    </div>

                    <div className="p-3 rounded-lg border border-primary/30 bg-primary/5">
                      <Label htmlFor="heures-suivants" className="flex items-center gap-2 text-primary">
                        <Clock className="h-3.5 w-3.5" />
                        Jours suivants ({joursOuvres - 1} jour{joursOuvres > 2 ? 's' : ''})
                      </Label>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          id="heures-suivants"
                          type="number"
                          min={0.5}
                          max={16}
                          step={0.5}
                          value={heuresJoursSuivants}
                          onChange={(e) => setHeuresJoursSuivants(Math.min(16, Math.max(0.5, parseFloat(e.target.value) || 8)))}
                          className="h-9 w-20"
                        />
                        <span className="text-sm text-muted-foreground">heures/jour</span>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Récapitulatif */}
                {nouveauChantierId && joursOuvres > 0 && (
                  <div className="flex items-start gap-2 p-2 bg-green-500/10 border border-green-500/30 rounded-lg text-xs">
                    <span className="text-green-600 mt-0.5">✓</span>
                    <div className="flex flex-col gap-0.5">
                      {joursOuvres === 1 ? (
                        <span>Nouveau chantier : <strong>{heuresJour1}h</strong> aujourd'hui</span>
                      ) : (
                        <>
                          <span>Nouveau chantier aujourd'hui : <strong>{heuresJour1}h</strong></span>
                          <span>Puis : <strong>{heuresJoursSuivants}h/jour</strong> pendant {joursOuvres - 1} jour{joursOuvres > 2 ? 's' : ''}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !canConfirm}
            className={isRotationMode ? "bg-primary hover:bg-primary/90" : "bg-amber-600 hover:bg-amber-700"}
          >
            {isLoading
              ? (isRotationMode ? 'Rotation en cours...' : 'Retrait en cours...')
              : (isRotationMode ? 'Confirmer la rotation' : 'Confirmer le retrait')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
